/**
 * Add/Edit Service-category dialog (DNKADM-0052 U1) — a MUI `Dialog` that
 * creates/updates a `v2_service_category` via `createServiceCategory` /
 * `updateServiceCategory` (handoff §5.3).
 *
 * Fields: **name** (required), optional **description**, and the **booking entry
 * point** checkbox (`bookable`, DNKADM-0267 — display-only, never gates booking;
 * preserve-on-null: an untouched/unknown value is omitted from the body). Categories
 * are a labeling taxonomy decoupled from credits — there is NO bucket/credit field
 * here. `isActive` is NOT set here — activation is the list's deactivate/reactivate
 * affordance (§4.3).
 *
 * Client validation blocks Save while the name is empty. On a successful
 * create/update the parent **refetches** (the caller's `onSaved`); a validation
 * `400` / a unique-name `409` renders INLINE, anything else raises an error toast
 * (`classifyFormError`).
 */
import { useId, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
} from '@mui/material';
import {
  createServiceCategory,
  updateServiceCategory,
  type ServiceCategory,
  type ServiceCategoryInput,
} from '@/api/serviceCategories';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';

export interface ServiceCategoryDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The category being edited, or `null` to add a new one. */
  category: ServiceCategory | null;
  /** Close the dialog without saving. */
  onClose: () => void;
  /** Called after a successful create/update so the caller can refetch the list. */
  onSaved: () => void;
  /** Raise a non-blocking toast (upstream-error). */
  showToast: ShowToast;
}

export function ServiceCategoryDialog(props: ServiceCategoryDialogProps) {
  // Remount the inner form per open / per edited category so its inputs reseed.
  if (!props.open) return null;
  return <ServiceCategoryDialogForm key={props.category?.id ?? 'new'} {...props} />;
}

/** Trim a string and return `undefined` when empty (omit the optional key). */
function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function ServiceCategoryDialogForm({
  category,
  onClose,
  onSaved,
  showToast,
}: Omit<ServiceCategoryDialogProps, 'open'>) {
  const ids = useId();
  const isEdit = category !== null;

  const [name, setName] = useState(category?.name ?? '');
  const [description, setDescription] = useState(category?.description ?? '');
  // Booking entry point (DNKADM-0267) — preserve-on-null: `undefined` (never touched,
  // and no stored value to preload) is OMITTED on save; the checkbox displays the
  // server-default `true` in that state.
  const [bookable, setBookable] = useState<boolean | undefined>(category?.bookable);

  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function validate(): ServiceCategoryInput | null {
    const trimmedName = name.trim();
    if (trimmedName === '') {
      setNameError('Enter a category name.');
      return null;
    }
    setNameError(undefined);

    const input: ServiceCategoryInput = { name: trimmedName };
    const desc = optional(description);
    if (desc !== undefined) input.description = desc;
    // DNKADM-0267 preserve-on-null: only an actual value goes out (create → the
    // server defaults true; update → an omitted key preserves the stored value).
    if (bookable !== undefined) input.bookable = bookable;
    return input;
  }

  async function handleSave() {
    setServerError(null);
    const input = validate();
    if (!input) return;
    setSaving(true);
    try {
      if (isEdit && category) await updateServiceCategory(category.id, input);
      else await createServiceCategory(input);
      // DNKADM-0320: confirm the save (after the write resolves; one toast per action).
      showToast(isEdit ? 'Category saved' : 'Category added', 'success');
      onSaved();
    } catch (err) {
      const view = classifyFormError(
        err,
        'The category couldn’t be saved. Please try again.',
      );
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const titleId = `${ids}-title`;

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{isEdit ? 'Edit category' : 'Add category'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            size="small"
            required
            error={Boolean(nameError)}
            helperText={nameError}
            slotProps={{ htmlInput: { 'aria-label': 'Category name' } }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving}
            size="small"
            multiline
            minRows={2}
            slotProps={{ htmlInput: { 'aria-label': 'Description' } }}
          />
          <Stack spacing={0}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={bookable ?? true}
                  onChange={(e) => setBookable(e.target.checked)}
                  disabled={saving}
                  slotProps={{
                    input: { 'aria-label': 'Show this category as a booking entry point' },
                  }}
                />
              }
              label="Show this category as a booking entry point"
            />
            <FormHelperText>
              Display-only — unchecking hides the entry point but doesn’t block booking
              the services in it.
            </FormHelperText>
          </Stack>

          {serverError && (
            <Alert severity="error" role="alert">
              {serverError}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save category' : 'Add category'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
